import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import { Anchor, Flex, Heading, List, Paragraph } from '@zillow/constellation';
import { ERROR_STEP, SUCCESS_STEP, useCurrentUserAsEmployee } from '../ApplicationManagerModal.util';
import { useMutation } from '@apollo/client';
import { BECOME_APPLICATION_MANAGER_MUTATION, getIsOnlyAdmin } from '../../../utils/businessVerification';

const SINGLE_ADMIN = {
  HEADING: "You're the application manager",
  SUB_HEADING: "You'll be able to give people permission to access applications.",
  PRE_DESCRIPTION: null,
  LIST_HEADING: 'As the application manager, you will:',
  POST_DESCRIPTION:
    "When you give a user applications permission, they'll be able to review applications for their assigned properties.",
  QUESTION: null,
};

const MULTIPLE_ADMINS = {
  HEADING: 'Become application manager?',
  SUB_HEADING: "Your account doesn't currently have an application manager assigned.",
  PRE_DESCRIPTION:
    'Your account now allows up to 5 people to access applications. The application manager is the only person on the account who can assign those permissions.',
  LIST_HEADING: 'The application manager will:',
  POST_DESCRIPTION: null,
  QUESTION: 'Would you like the application manager role assigned to you?',
};

function Disclaimer() {
  return (
    <Paragraph fontType="finePrint">
      {/* TODO proper link */}
      By providing access to representatives, you agree that you and your representatives will comply with our{' '}
      <Anchor href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms">Rental User Terms</Anchor> and that you
      are responsible for any use and access of the application, credit report, and background check by you and your
      representative.
    </Paragraph>
  );
}

function useBecomeApplicationManagerIfEligible(id, employees) {
  const { onRequestStep } = useWizard();
  const [becomeApplicationManagerMutation] = useMutation(BECOME_APPLICATION_MANAGER_MUTATION);
  const {
    role,
    contactInfo: { firstName, lastName },
  } = useCurrentUserAsEmployee(employees);
  const { currentStep } = useWizard();
  const isOnlyAdmin = getIsOnlyAdmin(employees);
  const isCurrentStep = currentStep === id;
  useEffect(() => {
    async function becomeApplicationManager() {
      try {
        const { errors } = await becomeApplicationManagerMutation({ variables: { firstName, lastName } });
        if (errors) {
          onRequestStep(ERROR_STEP);
        }
      } catch {
        onRequestStep(ERROR_STEP);
      }
    }
    if (role !== 'applicationManager' && firstName && lastName && isOnlyAdmin && isCurrentStep) {
      becomeApplicationManager();
    }
  }, [role, firstName, lastName, isOnlyAdmin, becomeApplicationManagerMutation, onRequestStep, isCurrentStep]);
}

function useOnRequestNext(employees) {
  const { onRequestStep } = useWizard();
  const [becomeApplicationManager] = useMutation(BECOME_APPLICATION_MANAGER_MUTATION);
  const {
    role,
    contactInfo: { firstName, lastName },
  } = useCurrentUserAsEmployee(employees);
  const isOnlyAdmin = getIsOnlyAdmin(employees);
  if (role !== 'applicationManager' && firstName && lastName) {
    if (isOnlyAdmin) {
      return (navigationController) => {
        navigationController.abort();
        onRequestStep(SUCCESS_STEP);
      };
    }
    return async (navigationController) => {
      try {
        const { errors } = await becomeApplicationManager({ variables: { firstName, lastName } });
        navigationController.abort();
        onRequestStep(errors ? ERROR_STEP : SUCCESS_STEP);
      } catch {
        onRequestStep(ERROR_STEP);
      }
    };
  }
  return () => {};
}

export default function LandingStep({ id, employees }) {
  useBecomeApplicationManagerIfEligible(id, employees);
  const onRequestNext = useOnRequestNext(employees);

  const isOnlyAdmin = getIsOnlyAdmin(employees);
  const config = isOnlyAdmin ? SINGLE_ADMIN : MULTIPLE_ADMINS;
  return (
    <WizardStep id={id} label="Landing" onRequestNext={onRequestNext}>
      <Flex display="flex" gap="sm" flexDirection="column">
        <Heading as="h1" level={4}>
          {config.HEADING}
        </Heading>
        <Paragraph fontType="bodySmallHeading">{config.SUB_HEADING}</Paragraph>
        {config.PRE_DESCRIPTION && <Paragraph marginBottom="xs">{config.PRE_DESCRIPTION}</Paragraph>}
        <Paragraph>{config.LIST_HEADING}</Paragraph>
        <List>
          <List.Item>Receive permission requests from teammates</List.Item>
          <List.Item>Manage application access permissions for up to 5 users on the account</List.Item>
          <List.Item>Be listed as the person viewing applications</List.Item>
        </List>
        {config.POST_DESCRIPTION && <Paragraph marginTop="md">{config.POST_DESCRIPTION}</Paragraph>}
        {config.QUESTION && <Paragraph fontType="bodySmallHeading">{config.QUESTION}</Paragraph>}
        <Disclaimer />
      </Flex>
    </WizardStep>
  );
}

LandingStep.propTypes = {
  id: PropTypes.string.isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
    }),
  ),
};
